import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createApiClient, getBalance, type ApiClient } from '../api';
import { config } from '../../config';
import { useSession } from '../session';
import { emit, isIncomingEvent } from '../postMessage';
import type { RefreshOptions, WalletContextValue, WalletState } from './types';
import { WalletContext } from './WalletContext';
import { useBalanceKeepalive } from './useBalanceKeepalive';

// Waits before each re-check after a sell, counted from the previous read's
// answer. The backend queues the /credit and answers the sell first, so the
// read right after it can reach the operator before the credit does; on
// staging the credit landed ≤1.7 s after the sale.
const CREDIT_RECHECK_GAPS_MS = [1_500, 2_500];

// Strict-mode-safe seed — state never carries an api error across remounts.
const INITIAL: WalletState = {
  balance_cents: null,
  currency: 'USD',
  loading: true,
  error: null,
};

export function WalletProvider({
  children,
  balanceKeepaliveSec = 0,
}: {
  children: ReactNode;
  /** Per-operator casino keepalive (design_system); 0 = no polling. */
  balanceKeepaliveSec?: number;
}) {
  const session = useSession();
  const [state, setState] = useState<WalletState>(INITIAL);

  // Own API client — own responsibilities. Stable getToken/getEmbedKey keep
  // the client identity fixed across token adoption.
  const client = useMemo<ApiClient>(
    () =>
      createApiClient({
        baseUrl: config.apiUrl,
        getToken: session.getToken,
        getEmbedKey: () => session.embedKey,
      }),
    [session.getToken, session.embedKey],
  );

  const clientRef = useRef(client);
  clientRef.current = client;

  // Track the previous balance across refreshes without triggering
  // re-renders — useRef avoids the re-render loop that a dependency on
  // `state.balance_cents` would introduce.
  const prevBalanceRef = useRef<number | null>(null);

  const fetchBalance = useCallback(async (): Promise<number | null> => {
    // Read the token LIVE (getToken is a stable ref-reader), never a snapshot.
    // A snapshot would let an async caller that captured `refresh` while
    // anonymous (e.g. a purchase started pre-auth in useGamePurchase) run the
    // stale null-token branch AFTER auth and wrongly null the balance.
    if (!session.getToken()) {
      // Anonymous (no session): settle into an empty, non-loading state so
      // the boot routing in App's Inner isn't blocked waiting on a fetch
      // that will never happen.
      setState((s) => ({ ...s, balance_cents: null, loading: false, error: null }));
      return null;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const res = await getBalance(clientRef.current);
      setState({
        balance_cents: res.balance_cents,
        currency: res.currency,
        loading: false,
        error: null,
      });

      // Dev-only: log balance transitions so debit/credit are easy to
      // eyeball during purchase / sell flows. Delta tells you whether the
      // wallet went up (credit) or down (debit) since the last refresh.
      if (import.meta.env.DEV) {
        const prev = prevBalanceRef.current;
        const delta = prev == null ? null : res.balance_cents - prev;
        const kind =
          delta == null
            ? 'initial'
            : delta > 0
              ? 'credit'
              : delta < 0
                ? 'debit'
                : 'no-change';
        console.log(
          `%c[wallet] ${kind}`,
          'color: #38BDF8; font-weight: 600',
          {
            balance_cents: res.balance_cents,
            currency: res.currency,
            delta_cents: delta,
          },
        );
      }
      prevBalanceRef.current = res.balance_cents;

      emit('balance_updated', {
        balance_cents: res.balance_cents,
        currency: res.currency,
      });
      return res.balance_cents;
    } catch (err) {
      setState((s) => ({
        ...s,
        loading: false,
        error: err instanceof Error ? err.message : 'balance fetch failed',
      }));
      // Null (not the stale state value) so callers gating on the result
      // fail open rather than act on a balance we couldn't confirm.
      return null;
    }
  }, [session.getToken]);

  // Bumped by every expectCredit refresh and on unmount — a re-check loop
  // whose generation is no longer current quits without fetching.
  const creditRecheckGenRef = useRef(0);
  useEffect(
    () => () => {
      creditRecheckGenRef.current += 1;
    },
    [],
  );

  const recheckForCredit = useCallback(
    async (gen: number) => {
      for (const gap of CREDIT_RECHECK_GAPS_MS) {
        await new Promise((resolve) => window.setTimeout(resolve, gap));
        if (creditRecheckGenRef.current !== gen) return;
        // Compare with the last figure seen, not the pre-sell one: a purchase
        // in between (sell → rip the next pack) moves the balance down while
        // the credit is still on its way.
        const seen = prevBalanceRef.current;
        const fresh = await fetchBalance();
        if (fresh === null || fresh !== seen) return;
      }
    },
    [fetchBalance],
  );

  const refresh = useCallback(
    async ({ expectCredit = false }: RefreshOptions = {}): Promise<number | null> => {
      if (!expectCredit) return fetchBalance();
      const gen = ++creditRecheckGenRef.current;
      const before = prevBalanceRef.current;
      const fresh = await fetchBalance();
      // Moved already → the credit beat this read. Null → no session / failed.
      if (fresh !== null && fresh === before) void recheckForCredit(gen);
      return fresh;
    },
    [fetchBalance, recheckForCredit],
  );

  // Token-keyed fetch: once per distinct token value. With a boot token this
  // fires exactly once (the old one-shot behavior); on token adoption (embed
  // auth) it refetches; with no token `refresh` early-returns into a settled
  // empty state. The Symbol seed guarantees the first run always fires, even
  // when the boot token is null.
  const lastTokenRef = useRef<symbol | string | null>(Symbol('init'));
  useEffect(() => {
    if (lastTokenRef.current === session.token) return;
    lastTokenRef.current = session.token;
    void refresh();
  }, [session.token, refresh]);

  // Parent → iframe: { source: 'repackz-parent', type: 'balance_changed' }.
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (!isIncomingEvent(e.data)) return;
      if (e.data.type === 'balance_changed') void refresh();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [refresh]);

  // Players are allowed to play in several tabs at once, and nothing syncs
  // the balance between them: spending in one tab leaves every other tab
  // showing the pre-purchase number. The parent isn't required to send
  // `balance_changed` (it isn't part of the operator contract), so coming
  // back to a tab is our only reliable cue that the displayed figure may be
  // out of date.
  useEffect(() => {
    function onVisible() {
      if (document.visibilityState === 'visible') void refresh();
    }
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  // Seamless casinos that expire idle sessions: keep the operator token alive
  // while the game is open (per-operator interval, off by default).
  useBalanceKeepalive(balanceKeepaliveSec, session.token !== null, refresh);

  // A ref, not state: nothing renders from it — the pill reads it on mount.
  const creditFlashQueuedRef = useRef(false);
  const queueCreditFlash = useCallback(() => {
    creditFlashQueuedRef.current = true;
  }, []);
  const hasQueuedCreditFlash = useCallback(() => creditFlashQueuedRef.current, []);
  const clearCreditFlash = useCallback(() => {
    creditFlashQueuedRef.current = false;
  }, []);

  const value = useMemo<WalletContextValue>(
    () => ({ ...state, refresh, queueCreditFlash, hasQueuedCreditFlash, clearCreditFlash }),
    [state, refresh, queueCreditFlash, hasQueuedCreditFlash, clearCreditFlash],
  );

  return <WalletContext value={value}>{children}</WalletContext>;
}
